// lib/takeoff/sheetLegend.js
// Data for the printed sheet legend: one entry per drawing sheet, listing the assemblies used on that sheet
// (colour, tag, name, quantity on THIS sheet, height, description lines, priced estimate items).

const n0 = (v) =>
  Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const feetIn = (f) => {
  if (!f) return "";
  let ft = Math.floor(f);
  let inch = Math.round((f - ft) * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return `${ft}'-${inch}"`;
};

// → [{ page, label, area, rows: [{ cond, qty, height, desc, items, type }] }]
export function buildSheetLegends({
  conditions = [],
  shapes = [],
  pageNames = {},
  excludedPages = [],
  pageBidAreas = {},
  mappings = [],
  metricsFor, // (cond, pageShapes) => metrics from computeCondMetrics
}) {
  const excluded = new Set(excludedPages);
  const pages = [...new Set(shapes.map((s) => s.page))]
    .filter((p) => !excluded.has(p))
    .sort((a, b) => a - b);
  const condById = new Map(conditions.map((c) => [c.id, c]));
  const typeOrder = ["linear", "area", "count"];

  return pages
    .map((page) => {
      const onPage = shapes.filter((s) => s.page === page);
      const ids = [...new Set(onPage.map((s) => s.conditionId))].filter((id) =>
        condById.has(id),
      );
      const rows = ids
        .map((id) => {
          const cond = condById.get(id);
          const mine = onPage.filter((s) => s.conditionId === id);
          const m = metricsFor(cond, onPage);
          let qty = "";
          if (m?.uncal) qty = "sheet scale not set";
          else if (cond.type === "area") qty = `${n0(m?.sf)} SF`;
          else if (cond.type === "count") qty = `${n0(m?.count)} EA`;
          else
            qty = [`${n0(m?.lf)} LF`, m?.sf > 0 ? `${n0(m.sf)} SF` : ""]
              .filter(Boolean)
              .join(" · ");
          const hs = [
            ...new Set(
              mine
                .map((s) => Number(s.meta?.height) || Number(cond.height) || 0)
                .filter(Boolean),
            ),
          ].sort((a, b) => a - b);
          const height =
            cond.type !== "linear" || !hs.length
              ? ""
              : hs.length === 1
                ? feetIn(hs[0])
                : `${feetIn(hs[0])} – ${feetIn(hs[hs.length - 1])}`;
          const mp = mappings.find((x) => x.conditionId === id);
          return {
            cond,
            qty,
            height,
            desc: String(cond.description || "")
              .split(/\r?\n/)
              .map((l) => l.trim())
              .filter(Boolean),
            items: (mp?.items || []).map((i) => i.title || i.itemCode),
            type: cond.type,
            sort: m?.sf || m?.lf || m?.count || 0,
          };
        })
        // walls, then areas, then counts; biggest first
        .sort(
          (a, b) =>
            typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type) ||
            b.sort - a.sort,
        );
      return {
        page,
        label: pageNames[page] || pageNames[String(page)] || `Sheet ${page}`,
        area: String(
          pageBidAreas[page] ?? pageBidAreas[String(page)] ?? "",
        ).trim(),
        rows,
      };
    })
    .filter((s) => s.rows.length);
}
